import { API_BASE_URL } from '../config/environment';

// Types
export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  role?: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
}

export interface Product {
  id: number;
  name: string;
  description?: string;
  price: number;
  originalPrice?: number;
  images: string[];
  hoverImage?: string;
  size?: string;
  inStock: boolean;
  isSold?: boolean;
  categoryId: number;
  category?: Category;
  categories?: Category[];
  condition?: string;
  fitReference?: {
    id: number;
    name: string;
    type: 'dressform' | 'model';
    measurements: { label: string; value: string }[];
  } | null;
}

export interface Category {
  id: number;
  name: string;
  description?: string;
  image?: string;
}

export interface CartItem {
  id: number;
  userId: number;
  productId: number;
  quantity: number;
  size?: string;
  color?: string;
  product?: Product;
}

export interface OrderData {
  shippingInfo: any;
  billingInfo: any;
  paymentInfo: any;
  items: CartItem[];
  totals: {
    subtotal: number;
    discount: number;
    shipping: number;
    total: number;
  };
}

export interface Order {
  id: number;
  orderNumber: string;
  status: string;
  totalAmount: number;
  paymentMethod: string;
  createdAt: string;
  shippingAddress: string;
  items: CartItem[];
  redirectUrl?: string;
}

export interface AuthResponse {
  message: string;
  user: User;
  token: string;
}

// Helper function to get auth token
const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

// Helper function to set auth headers
const getAuthHeaders = (): { [key: string]: string } => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// API Functions
export const api = {
  // Authentication
  async login(data: LoginData): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    const result = await response.json();
    
    // Store token in localStorage
    if (result.token) {
      localStorage.setItem('authToken', result.token);
      localStorage.setItem('user', JSON.stringify(result.user));
    }

    return result;
  },

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Registration failed');
    }

    const result = await response.json();
    
    // Store token in localStorage
    if (result.token) {
      localStorage.setItem('authToken', result.token);
      localStorage.setItem('user', JSON.stringify(result.user));
    }

    return result;
  },

  logout(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  },

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated(): boolean {
    return !!getAuthToken();
  },

  // Products
  async getProducts(params?: {
    category?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ products: Product[]; pagination: any }> {
    const queryParams = new URLSearchParams();
    
    if (params?.category) queryParams.append('category', params.category);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const response = await fetch(`${API_BASE_URL}/products?${queryParams}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch products');
    }

    return response.json();
  },

  async getProduct(id: number): Promise<Product> {
    const response = await fetch(`${API_BASE_URL}/products/${id}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch product');
    }

    return response.json();
  },

  async getRandomProducts(excludeId?: number): Promise<Product[]> {
    const queryParams = new URLSearchParams();
    if (excludeId) {
      queryParams.append('excludeId', excludeId.toString());
    }

    const response = await fetch(`${API_BASE_URL}/products/random?${queryParams}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch random products');
    }

    return response.json();
  },

  async getHotTrendProducts(): Promise<Product[]> {
    // Get hot trend product IDs from localStorage (in real app, this would come from backend)
    const hotTrendIds = localStorage.getItem('hotTrendProducts');
    if (!hotTrendIds) {
      return [];
    }

    const productIds = JSON.parse(hotTrendIds);
    if (!Array.isArray(productIds) || productIds.length === 0) {
      return [];
    }

    // Fetch all products and filter by hot trend IDs
    try {
      const { products } = await this.getProducts({ limit: 1000 });
      return products.filter(product => productIds.includes(product.id));
    } catch (error) {
      return [];
    }
  },

  async getCategories(): Promise<Category[]> {
    const response = await fetch(`${API_BASE_URL}/categories`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch categories');
    }

    const data = await response.json();
    return data.categories || data; // Handle both formats
  },

  // Get available sizes for a specific category
  async getSizesByCategory(categoryId?: number): Promise<string[]> {
    const url = categoryId 
      ? `${API_BASE_URL}/products/sizes?categoryId=${categoryId}`
      : `${API_BASE_URL}/products/sizes`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch sizes');
    }
    
    const data = await response.json();
    return data.sizes || [];
  },

  async getCategoryStatistics(): Promise<{ [categoryName: string]: { count: number; minPrice: number; maxPrice: number } }> {
    try {
      // Fetch all products to calculate statistics
      const { products } = await this.getProducts({ limit: 1000 });
      
      // Define category mapping with real IDs from database
      const categoryMapping = {
        'Dresses': 2,    // Dress category ID
        'Tops': 1,       // Tops category ID  
        'Bottoms': 7,    // Bottoms category ID
        'Accessories': 3 // Accessories category ID
      };
      
      const stats: { [categoryName: string]: { count: number; minPrice: number; maxPrice: number } } = {};
      
      // Initialize all categories with zero values
      Object.keys(categoryMapping).forEach(categoryName => {
        stats[categoryName] = {
          count: 0,
          minPrice: 0,
          maxPrice: 0
        };
      });
      
      products.forEach(product => {
        if (!product.inStock) return; // Only count in-stock products
        
        const price = Number(product.price);
        const categoryId = product.categoryId;
        
        // Find which category this product belongs to
        Object.entries(categoryMapping).forEach(([categoryName, categoryIdFromMapping]) => {
          if (categoryId === categoryIdFromMapping) {
            if (stats[categoryName].count === 0) {
              // First product in this category
              stats[categoryName] = {
                count: 1,
                minPrice: price,
                maxPrice: price
              };
            } else {
              // Additional product in this category
              stats[categoryName].count++;
              stats[categoryName].minPrice = Math.min(stats[categoryName].minPrice, price);
              stats[categoryName].maxPrice = Math.max(stats[categoryName].maxPrice, price);
            }
          }
        });
      });
      
      return stats;
    } catch (error) {
      return {};
    }
  },

  async getShopTheLookData(): Promise<any> {
    try {
      // Get shop the look data for homepage - only published outfits
      const response = await fetch(`${API_BASE_URL}/admin/shop-the-look`);
      
      if (!response.ok) {
        return null;
      }
      
      const outfit = await response.json();
      return outfit;
    } catch (error) {
      console.error('Failed to fetch shop the look data:', error);
      return null;
    }
  },

  // Cart
  async getCart(): Promise<CartItem[]> {
    const response = await fetch(`${API_BASE_URL}/cart`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch cart');
    }

    return response.json();
  },

  async addToCart(item: {
    productId: number;
    quantity: number;
    size?: string;
    color?: string;
  }): Promise<{ message: string; cartItem: CartItem }> {
    const response = await fetch(`${API_BASE_URL}/cart`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(item),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to add item to cart');
    }

    return response.json();
  },

  async updateCartItem(id: number, quantity: number): Promise<{ message: string; cartItem: CartItem }> {
    const response = await fetch(`${API_BASE_URL}/cart/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ quantity }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update cart item');
    }

    return response.json();
  },

  async removeFromCart(id: number): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/cart/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to remove item from cart');
    }

    return response.json();
  },

  async clearCart(): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/cart`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to clear cart');
    }

    return response.json();
  },

  // Favorites
  async getFavorites(): Promise<Product[]> {
    const response = await fetch(`${API_BASE_URL}/favorites`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch favorites');
    }

    return response.json();
  },

  async addToFavorites(productId: number): Promise<{ message: string; favorite: Product }> {
    const response = await fetch(`${API_BASE_URL}/favorites`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ productId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to add to favorites');
    }

    return response.json();
  },

  async removeFromFavorites(productId: number): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/favorites/${productId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to remove from favorites');
    }

    return response.json();
  },

  async checkFavorite(productId: number): Promise<{ isFavorite: boolean }> {
    const response = await fetch(`${API_BASE_URL}/favorites/check/${productId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to check favorite status');
    }

    return response.json();
  },

  async clearFavorites(): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/favorites`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to clear favorites');
    }

    return response.json();
  },

  // Orders
  async createOrder(orderData: OrderData): Promise<Order> {
    const response = await fetch(`${API_BASE_URL}/orders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(orderData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create order');
    }

    const result = await response.json();
    return result; // Return the full response with orderNumber
  },

  async confirmPayment(orderNumber: string): Promise<{ paymentStatus: string }> {
    const response = await fetch(`${API_BASE_URL}/orders/${orderNumber}/confirm-payment`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to confirm payment');
    }

    return response.json();
  },

  async getOrder(orderNumber: string): Promise<Order> {
    const response = await fetch(`${API_BASE_URL}/orders/${orderNumber}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch order');
    }

    return response.json();
  },

  async getUserOrders(): Promise<Order[]> {
    const response = await fetch(`${API_BASE_URL}/orders/user`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch orders');
    }

    return response.json();
  },
};